/**
 * Pure logic for the Studio side of scheduled broadcasts (OrchestratorAPI #341
 * + #343): schedule a broadcast for a future `sendAt`, list what is pending,
 * cancel before it fires.
 *
 * Lives outside the components for the same reason `broadcastChannels.ts`
 * does — there is no render harness in this package (vitest runs in `node`),
 * so anything worth getting wrong is tested here and the wiring is pinned
 * structurally in `components/groups/broadcasts/scheduledBroadcasts.contract.test.ts`.
 *
 * Three traps this module exists to not repeat, all real bugs elsewhere in
 * Studio before a similar module existed for them:
 *
 * 1. Comparing instants as ISO strings. `wallClockToIso` produces a real
 *    offset (`-05:00`, `+00:00`, ...), and two ISO strings with different
 *    offsets do NOT sort the same way lexicographically as they do by instant
 *    — see {@link sendAtIsInFuture}.
 * 2. Treating the 503 designed-state as a normal failure. Every
 *    scheduled-broadcast endpoint answers 503
 *    `{ code: "scheduled_broadcasts_unavailable" }` on an environment where
 *    migration 014 has not been applied — a routine, expected state, not an
 *    error to toast — see {@link isScheduledBroadcastsUnavailable}.
 * 3. Losing the 409 cancel race. Cancelling a row that already
 *    fired/failed/cancelled answers 409 with the row's CURRENT state in
 *    `broadcast` — the fix is to show that state, not a generic "that
 *    didn't work" — see {@link scheduledBroadcastConflict}.
 */
import { audienceLabel, audienceSegment } from '../components/groups/broadcasts/audience';
import { audienceKindLabel } from './audienceKind';
import { normalizeChannels } from './broadcastChannels';
import type { GroupScheduledBroadcast, ScheduleGroupBroadcastRequest } from '../types/groups';

/** Statuses the sweep can still act on. Everything else is terminal. */
const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['pending', 'sending']);

export type ScheduledStatusColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/** Human label + MUI chip color for a status, unknown statuses included. */
export function scheduledStatusMeta(status: string, lastError?: string | null): { label: string; color: ScheduledStatusColor } {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'info' };
    case 'sending':
      return { label: 'Sending…', color: 'info' };
    case 'sent':
      return { label: 'Sent', color: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    case 'cancelled':
      return { label: 'Cancelled', color: 'default' };
    case 'skipped':
      // NOT a failure and never red: an RSVP audience that resolved to nobody
      // when the row fired ("everyone had replied") — the reminder did exactly
      // what it was for. `lastError` carries the API's sentence; the chip
      // only picks between its two known readings.
      return {
        label: /nobody/i.test(lastError ?? '') ? 'Skipped — nobody was going yet' : 'Skipped — everyone had replied',
        color: 'default',
      };
    default:
      return { label: status || 'Unknown', color: 'default' };
  }
}

/**
 * What the composer posts, built the same way the interactive send builds
 * `SendGroupBroadcastRequest` — same channel normalization, same "SMS copy
 * only when SMS is picked" guard, same segment resolution — plus `sendAt`.
 * Kept as one function so the two request shapes cannot drift apart.
 */
export interface ScheduleRequestInput {
  subject: string;
  body: string;
  channels: readonly string[];
  smsSelected: boolean;
  smsBody: string;
  /** The raw composer audience value (e.g. `AUDIENCE_ALL`), not yet resolved. */
  segment: string;
  inviteeIds?: string[];
  /**
   * An RSVP audience the SERVER resolves when the row fires (#394). When set it
   * travels ALONE: `segment` and `inviteeIds` are left off the request, because
   * the API 400s on the combination and an id list would bring back the frozen
   * answer the kind exists to remove.
   */
  audienceKind?: string | null;
  personalizeInviteLinks: boolean;
  /** ISO-8601 absolute instant, already pinned to the trip's zone. */
  sendAt: string;
  /** Reminder plan membership (#394): one client-minted UUID per plan, and which step this row is. */
  sequenceId?: string;
  sequenceStep?: string;
  /** How many steps the plan has (OrchestratorAPI 018). REQUIRED by the API whenever `sequenceId` is sent. */
  sequenceTotalSteps?: number;
}

export function buildScheduleRequest(input: ScheduleRequestInput): ScheduleGroupBroadcastRequest {
  return {
    subject: input.subject,
    body: input.body,
    // Never post an empty array: the API reads that as email-only, same trap
    // as the interactive send.
    channels: normalizeChannels(input.channels),
    // Copy for a channel that was not picked is a 400 at the API, and would
    // otherwise be how last week's SMS text ships the next time SMS is ticked.
    smsBody: input.smsSelected ? input.smsBody : null,
    ...(input.audienceKind
      ? { audienceKind: input.audienceKind }
      : { segment: audienceSegment(input.segment), inviteeIds: input.inviteeIds }),
    personalizeInviteLinks: input.personalizeInviteLinks,
    sendAt: input.sendAt,
    ...(input.sequenceId ? { sequenceId: input.sequenceId } : {}),
    ...(input.sequenceStep ? { sequenceStep: input.sequenceStep } : {}),
    ...(input.sequenceId && input.sequenceTotalSteps ? { sequenceTotalSteps: input.sequenceTotalSteps } : {}),
  };
}

/**
 * True when `sendAt` is strictly after `nowMs`, compared as INSTANTS.
 *
 * The naive version — `sendAt > new Date(nowMs).toISOString()` — looks
 * correct and is not: two ISO strings with different UTC offsets do not sort
 * the same way lexicographically as they do by the instant they name. A host
 * scheduling from a trip zone with a negative offset produces exactly that
 * shape (`...T23:30:00-05:00` vs `...T00:00:00+00:00`), so a string
 * comparison would have called a LATER instant "not in the future" — see the
 * mutation log in `scheduledBroadcasts.test.ts` for the concrete case this
 * caught.
 *
 * Returns false for anything unparsable — an unset or malformed `sendAt` is
 * never "in the future".
 */
export function sendAtIsInFuture(sendAt: string | null | undefined, nowMs: number = Date.now()): boolean {
  if (!sendAt) return false;
  const instant = Date.parse(sendAt);
  if (Number.isNaN(instant)) return false;
  return instant > nowMs;
}

/**
 * Client-side first line of defence for design decision #4 ("an audience the
 * roster says is empty is refused client-side"). Only meaningful for the
 * audiences that resolve to an explicit id list (`AUDIENCE_RSVP_GOING`,
 * `AUDIENCE_RSVP_NO_REPLY` — see `audience.ts`): `undefined` means "the
 * segment decides", which this function cannot evaluate without the roster,
 * so it says nothing and leaves the server's own 400 as the only check.
 */
export function scheduleEmptyAudienceMessage(
  inviteeIds: string[] | undefined,
  rosterMissing = false,
): string | null {
  // While the roster is loading or failed to load, `audienceInviteeIds`
  // resolves the RSVP audiences to [] — not because nobody matches, but
  // because nothing could be checked yet. Saying "nobody matches" then is a
  // confident wrong answer beside the roster field's own "reading the
  // roster…" hint; the button is already held by composeReady, so say nothing.
  if (rosterMissing) return null;
  if (inviteeIds && inviteeIds.length === 0) {
    return 'Nobody matches this audience right now, so there is nothing to schedule.';
  }
  return null;
}

/** Shape of the 503 designed-state body — see `ScheduledBroadcastsUnavailable` in GroupsController. */
interface ScheduledUnavailableBody {
  code?: unknown;
}

/**
 * True for the 503 designed state — migration 014 has not been applied on
 * this environment — and ONLY for it. A caller must render "Scheduled sends
 * aren't available on this environment yet" and hide/disable scheduling, never
 * a red error toast or a retry loop, for this one.
 */
export function isScheduledBroadcastsUnavailable(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: ScheduledUnavailableBody } })?.response;
  if (response?.status !== 503) return false;
  return response?.data?.code === 'scheduled_broadcasts_unavailable';
}

/**
 * Reads a 409 cancel conflict — `{ error, broadcast }` — and returns the
 * row's CURRENT state, or null when the error is not that shape (a 404, a
 * 403, a transport failure). The caller's job on a non-null result is to
 * refresh the list and show this row's real status, never a generic failure.
 */
export function scheduledBroadcastConflict(error: unknown): GroupScheduledBroadcast | null {
  const response = (error as { response?: { status?: number; data?: { broadcast?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const broadcast = response.data?.broadcast;
  return broadcast && typeof broadcast === 'object' ? (broadcast as GroupScheduledBroadcast) : null;
}

/** Only a `pending` row can be cancelled. `sending` is mid-flight — cancel would only race the sweep into a 409. */
export function canCancelScheduledBroadcast(row: Pick<GroupScheduledBroadcast, 'status'>): boolean {
  return row.status === 'pending';
}

/**
 * Pending/sending first (soonest `sendAt` first — that is the order a host
 * needs to audit what is about to go out), then the terminal rows in the
 * order the API returned them (newest first). A stable partition, not a
 * re-sort of the whole list: re-sorting the terminal half by `sendAt` would
 * scatter a "sent" row away from the "cancelled" row from the same day that
 * the API deliberately put next to it.
 */
export function sortScheduledBroadcasts(rows: readonly GroupScheduledBroadcast[]): GroupScheduledBroadcast[] {
  const active = rows.filter((row) => ACTIVE_STATUSES.has(row.status));
  const terminal = rows.filter((row) => !ACTIVE_STATUSES.has(row.status));
  active.sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt));
  return [...active, ...terminal];
}

/**
 * Whether the list should keep polling. The sweep fires due rows without any
 * client action, so a pending/sending row on screen can go stale with nobody
 * having clicked anything — wired to react-query's `refetchInterval`.
 */
export function scheduledBroadcastsShouldPoll(rows: readonly GroupScheduledBroadcast[] | undefined): boolean {
  return Boolean(rows?.some((row) => ACTIVE_STATUSES.has(row.status)));
}

/**
 * "42 guests (as of scheduling — may have changed since)." Never implies a live
 * count. An RSVP audience (`audienceKind`) is different in kind, not just in
 * wording: nobody is frozen at scheduling, so it says who is decided WHEN IT SENDS.
 */
export function scheduledAudienceNote(
  row: Pick<GroupScheduledBroadcast, 'requestedCount'> & { audienceKind?: string | null },
): string {
  if (row.audienceKind) return 'decided when it sends';
  const count = Number.isFinite(row.requestedCount) ? row.requestedCount : 0;
  return `${count} guest${count === 1 ? '' : 's'} (as of scheduling — may have changed since)`;
}

/** The audience a scheduled row names: its RSVP kind when it has one, else its segment. */
export function scheduledAudienceLabel(row: Pick<GroupScheduledBroadcast, 'segment' | 'audienceKind'>): string {
  return audienceKindLabel(row.audienceKind) ?? audienceLabel(row.segment);
}

const TERMINAL_SENT = new Set(['sent', 'failed']);

/**
 * True when a row that was waiting to fire has since fired (sent or failed).
 *
 * The sweep sends without any client action, so the only way Studio learns a
 * scheduled send went out is this list's own refetch. When it does, the Sent
 * history — a separate query with its own cache and a 30s staleTime — has to
 * be told, or the row says "Sent" while the history beside it still does not
 * show the broadcast until a reload. Found in live QA on staging.
 *
 * A row that is ALREADY sent on first load is not a transition: history was
 * fetched fresh on the same mount and already contains it.
 */
export function scheduledSendCompleted(
  previous: readonly Pick<GroupScheduledBroadcast, 'id' | 'status'>[] | undefined,
  next: readonly Pick<GroupScheduledBroadcast, 'id' | 'status'>[] | undefined,
): boolean {
  if (!previous || !next) return false;
  const before = new Map(previous.map((row) => [row.id, row.status]));
  return next.some((row) => {
    if (!TERMINAL_SENT.has(row.status)) return false;
    const was = before.get(row.id);
    return was !== undefined && !TERMINAL_SENT.has(was);
  });
}
